/**
 * @file Lecture des contraintes d'une formule (bornes de convives, salles imposées).
 * @module packages.common.utils.restaurant.formulaConstraints
 *
 * Source unique pour interpréter `Formula.constraints` côté front : le widget
 * client, la liste des réglages et la saisie backoffice affichent les mêmes
 * bornes à partir de ces helpers. Le libellé français sert aux écrans
 * dashboard (français en dur) ; le widget traduit lui-même à partir de la
 * forme structurée (`kind`). Le module déballe aussi la réponse de l'API des
 * formules d'un créneau (`readSlotFormulasResponse`), partagée par le widget
 * et le site public.
 */

/**
 * Forme structurée des bornes de convives d'une formule.
 *
 * @typedef {Object} GuestsBounds
 * @property {'exact'|'range'|'min'|'max'} kind - Nature de la contrainte
 * @property {number|null} min - Borne basse (inclusive)
 * @property {number|null} max - Borne haute (inclusive)
 */

/**
 * Normalise une borne de convives brute (document, champ de formulaire, query)
 * en entier strictement positif, sinon `null`.
 *
 * @param {*} value - Valeur brute (nombre, chaîne, null)
 * @return {number|null}
 */
export function parseGuestsBound(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Résout les bornes de convives d'une formule sous forme structurée.
 *
 * @param {Object} [constraints] - Bloc `constraints` d'une formule
 * @return {GuestsBounds|null} `null` si aucune borne n'est posée
 */
export function resolveGuestsBounds(constraints) {
  const min = parseGuestsBound(constraints?.guests?.min);
  const max = parseGuestsBound(constraints?.guests?.max);
  if (min === null && max === null) return null;
  if (min !== null && max !== null) {
    return {kind: min === max ? 'exact' : 'range', min, max};
  }
  return min !== null ? {kind: 'min', min, max: null} : {kind: 'max', min: null, max};
}

/**
 * Libellé français des bornes de convives, pour les écrans dashboard.
 *
 * @param {GuestsBounds|null} bounds - Bornes résolues
 * @return {string|null} Ex. « 2 personnes », « 2 à 4 personnes », « min. 6 personnes »
 */
export function formatGuestsBoundsFr(bounds) {
  if (!bounds) return null;
  const plural = (n) => (n > 1 ? 'personnes' : 'personne');
  switch (bounds.kind) {
    case 'exact':
      return `${bounds.min} ${plural(bounds.min)}`;
    case 'range':
      return `${bounds.min} à ${bounds.max} ${plural(bounds.max)}`;
    case 'min':
      return `min. ${bounds.min} ${plural(bounds.min)}`;
    case 'max':
      return `max. ${bounds.max} ${plural(bounds.max)}`;
    default:
      return null;
  }
}

/**
 * Indique si un nombre de convives respecte les bornes d'une formule.
 * Sans bornes ou sans nombre exploitable, la formule est considérée compatible.
 *
 * @param {Object} [constraints] - Bloc `constraints` d'une formule
 * @param {number} partySize - Nombre de convives
 * @return {boolean}
 */
export function matchesGuestsBounds(constraints, partySize) {
  const bounds = resolveGuestsBounds(constraints);
  const guests = Number.parseInt(partySize, 10);
  if (!bounds || !Number.isInteger(guests) || guests <= 0) return true;
  if (bounds.min !== null && guests < bounds.min) return false;
  if (bounds.max !== null && guests > bounds.max) return false;
  return true;
}

/**
 * Identifiants des salles imposées par une formule, sous forme de chaînes.
 *
 * @param {Object} [constraints] - Bloc `constraints` d'une formule
 * @return {string[]} Liste vide si la formule n'impose aucune salle
 */
export function resolveConstrainedAreaIds(constraints) {
  const areaIds = Array.isArray(constraints?.areaIds) ? constraints.areaIds : [];
  return areaIds.map((areaId) => (typeof areaId === 'object' && areaId ? String(areaId._id || '') : String(areaId))).filter(Boolean);
}

/**
 * Libellé des salles imposées par une formule, pour les puces du dashboard.
 * Une salle sans nom connu (chargement en cours, salle supprimée) prend le
 * libellé de repli ; les libellés identiques ne sont affichés qu'une fois.
 *
 * @param {Object} [constraints] - Bloc `constraints` d'une formule
 * @param {Object<string, string>} [areaNameById] - Nom des salles par identifiant
 * @param {string} unknownLabel - Libellé d'une salle sans nom connu
 * @return {string} Ex. « Salle, Terrasse » ; chaîne vide si aucune salle n'est imposée
 */
export function formatConstrainedAreaNames(constraints, areaNameById, unknownLabel) {
  const names = resolveConstrainedAreaIds(constraints).map((areaId) => areaNameById?.[areaId] || unknownLabel);
  return [...new Set(names)].join(', ');
}

/**
 * Salles que partagent les formules liées à des salles, avec la même règle
 * que le serveur à la création : les formules sans salle ne restreignent rien.
 *
 * @param {Array<Object>} formulas - Formules sélectionnées (avec `constraints`)
 * @return {string[]|null} Salles communes (vide si aucune), `null` si aucune formule n'impose de salle
 */
export function intersectConstrainedAreaIds(formulas) {
  const lists = (formulas || []).map((formula) => resolveConstrainedAreaIds(formula?.constraints)).filter((ids) => ids.length > 0);
  if (lists.length === 0) return null;
  return [...new Set(lists.reduce((acc, ids) => acc.filter((id) => ids.includes(id))))];
}

/**
 * Déballe la réponse de l'API « formules d'un créneau » (widget et site
 * public) : les formules choisissables et, à part, celles dont les salles sont
 * pleines à cette heure, affichées grisées. Réponse en échec = deux listes vides.
 *
 * @param {Object} [data] - Corps de la réponse `{success, formulas, unavailableFormulas}`
 * @return {{formulas: Array, unavailableFormulas: Array}}
 */
export function readSlotFormulasResponse(data) {
  if (!data?.success) return {formulas: [], unavailableFormulas: []};
  return {
    formulas: Array.isArray(data.formulas) ? data.formulas : [],
    unavailableFormulas: Array.isArray(data.unavailableFormulas) ? data.unavailableFormulas : [],
  };
}
